import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { EmployerRequestModalComponent } from './modal/employer-request-modal.component';
import {
  CertificateStatusChangeAction,
  CertificateStatusChangeEvent,
  EmployerRequest,
  EmployerRequestUpdate,
} from 'services/professional/certificates/certificates.interface';
import { ComputedFrom } from '@certemy/common';
import { CertificateRejectModalComponent } from 'components/modal-windows/certificate/reject-modal/certificate-reject-modal.component';
import { CertificateAssignRequestData } from 'components/certificate-assign-request/certificate-assign-request.component';
import { CertificatesService } from 'services/professional/certificates/certificates.service';
import { EMPLOYER_REQUEST_STATUS } from 'constants/status.constants';
import { constants } from 'constants/constants';
import { NEVER } from 'rxjs';
import { Selectors } from 'features/profiles-state/+state/profiles-state.state';
import { catchError, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { skipModalClose, SmartModalService } from '@certemy/ui-components';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'cm-employer-request',
  templateUrl: './employer-request.component.html',
  styleUrls: ['./employer-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmployerRequestComponent implements OnInit, OnDestroy {
  @Input() request: EmployerRequest;
  @Input() certificate: CertificateAssignRequestData;
  @Input() certificateStatus: number;
  @Input() showAvatar = false;
  @Output() certificateStatusChange = new EventEmitter<CertificateStatusChangeEvent>();
  isLoggedAsProfessional = false;

  constructor(
    private smartModalService: SmartModalService,
    private certificatesService: CertificatesService,
    private store: Store<any>,
  ) {}

  @ComputedFrom('request')
  get requestedBy() {
    return `${this.request.first_name} ${this.request.last_name} (Employer from ${this.request.organization_name})`;
  }

  ngOnInit() {
    this.store
      .select(Selectors.currentProfile)
      .pipe(take(1))
      .subscribe((profile) => (this.isLoggedAsProfessional = !!profile.origin));
  }

  ngOnDestroy() {}

  openAcceptModal(event) {
    event.stopPropagation();
    this.smartModalService
      .open(
        EmployerRequestModalComponent,
        {
          agreement_text: this.request.agreement_text,
          requestedBy: this.requestedBy,
          requestedByImage: this.request.image,
        },
        { size: 'lg' },
      )
      .pipe(
        untilDestroyed(this),
        skipModalClose(),
        catchError(() => NEVER),
      )
      .subscribe(({ payload }) =>
        this.updateRequestStatus({
          status_id: EMPLOYER_REQUEST_STATUS.ACCEPTED,
          digital_signature: payload,
        }),
      );
  }

  openRejectModal(event) {
    event.stopPropagation();

    this.smartModalService
      .open(
        CertificateRejectModalComponent,
        {
          isLoggedAsProfessional: this.isLoggedAsProfessional,
          certificate: this.certificate,
        },
        { size: 'lg' },
      )
      .pipe(
        untilDestroyed(this),
        skipModalClose(),
        catchError(() => NEVER),
      )
      .subscribe(() => this.updateRequestStatus({ status_id: EMPLOYER_REQUEST_STATUS.DECLINED }));
  }

  updateRequestStatus(requestUpdate: EmployerRequestUpdate) {
    const action =
      requestUpdate.status_id === EMPLOYER_REQUEST_STATUS.ACCEPTED
        ? CertificateStatusChangeAction.ACCEPT
        : CertificateStatusChangeAction.REJECT;
    this.certificatesService
      .updateEmployerRequestStatus(this.request.employer_certification_request_id, requestUpdate)
      .subscribe(() =>
        this.certificateStatusChange.emit({
          certificate_id: this.certificate.certificate_id,
          action,
        }),
      );
  }

  @ComputedFrom('request')
  get shouldShow() {
    return !!this.request;
  }

  @ComputedFrom('certificateStatus')
  get requestOrAssignMessage() {
    return this.certificateStatus === constants.CERTIFICATE_STATUSES.IMPOSED
      ? 'has assigned this credential. In case you accept this credential, Employer will have access to credential status, attachments, steps.'
      : 'has requested access to this credential';
  }
}
